import { Fragment, useState } from "react";
import { LabOrderContent } from "@/features/labs/LabOrderContent";
import {
  labOrderLabel,
  labReportUrl,
  labTrackingUrl,
} from "@/features/labs/labPresentation";
import {
  useApproveLabAddressOverride,
  useClinicalPatientLabOrders,
} from "@/lib/api/clinicalAdminHooks";
import type { AdminLabOrder } from "@/types/adminLabs";
import {
  Badge,
  Card,
  ErrorState,
  LoadingState,
  Td,
  Th,
  fmtDateTime,
  fmtMoney,
} from "./ui";

type LabTab = "orders" | "billing" | "intake" | "documents";

/** Lab data uses the patient workspace's existing cards/tables and lab detail. */
export function AdminLabRecords({
  patientId,
  tab = "orders",
}: {
  patientId: string;
  tab?: LabTab;
}) {
  const query = useClinicalPatientLabOrders(patientId);
  const [expanded, setExpanded] = useState<string | null>(null);
  if (query.isLoading) return <LoadingState label="Loading blood tests…" />;
  if (query.isError)
    return <ErrorState>Blood test records could not be loaded.</ErrorState>;
  const orders = query.data?.orders ?? [];
  if (!orders.length) return null;
  const toggle = (id: string) => setExpanded(expanded === id ? null : id);
  const action = (
    <button
      type="button"
      className="text-accent type-label-sm hover:underline"
      onClick={() => void query.refetch()}
    >
      Refresh
    </button>
  );

  if (tab === "billing")
    return (
      <div className="ph-no-capture">
        <Card title={`Blood test purchases (${orders.length})`} action={action}>
          <div className="overflow-x-auto">
            <table className="w-full type-label-sm">
              <thead className="bg-sunken">
                <tr>
                  <Th>Purchase</Th>
                  <Th>Payment</Th>
                  <Th>Amount</Th>
                  <Th>Paid</Th>
                  <Th>Payment receipt</Th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id} className="border-t border-line-subtle">
                    <Td>
                      {order.product_name}
                      <div className="text-ink-muted">
                        {order.order_number} · One-time purchase
                      </div>
                    </Td>
                    <Td>
                      <Badge
                        tone={
                          order.receipt.status === "paid" ? "good" : "neutral"
                        }
                      >
                        {order.receipt.status}
                      </Badge>
                    </Td>
                    <Td>
                      {fmtMoney(
                        order.receipt.amount_cents,
                        order.receipt.currency,
                      )}
                    </Td>
                    <Td>{fmtDateTime(order.receipt.paid_at)}</Td>
                    <Td>
                      <span className="font-mono break-all">
                        {order.receipt.payment_intent_id || "Payment pending"}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    );

  if (tab === "intake")
    return (
      <div className="ph-no-capture">
        <Card title={`Blood test intakes (${orders.length})`} action={action}>
          {orders.map((order) => (
            <div
              key={order.id}
              className="border-t border-line-subtle first:border-0"
            >
              <button
                type="button"
                className="w-full flex items-center justify-between gap-12 px-15 py-12 hover:bg-sunken"
                aria-expanded={expanded === order.id}
                onClick={() => toggle(order.id)}
              >
                <span className="type-label-sm font-medium">
                  {order.product_name} · {order.order_number}
                </span>
                <span className="type-label-sm text-ink-muted">
                  {fmtDateTime(order.created_at)}
                </span>
              </button>
              {expanded === order.id && <LabIntakeAnswers order={order} />}
            </div>
          ))}
        </Card>
      </div>
    );

  if (tab === "documents")
    return (
      <div className="ph-no-capture flex flex-col gap-16">
        <Card title="Blood test documents" action={action}>
          <div className="overflow-x-auto">
            <table className="w-full type-label-sm">
              <thead className="bg-sunken">
                <tr>
                  <Th>Document</Th>
                  <Th>Chart delivery</Th>
                  <Th>Healthie document</Th>
                  <Th>Created</Th>
                  <Th> </Th>
                </tr>
              </thead>
              <tbody>
                {orders.flatMap((order) =>
                  order.chart_documents.map((document) => {
                    const url = labReportUrl(document.download_url);
                    return (
                      <tr
                        key={document.id}
                        className="border-t border-line-subtle"
                      >
                        <Td>
                          {order.order_number} ·{" "}
                          {document.kind === "intake"
                            ? "Lab intake"
                            : "Lab result chart copy"}
                        </Td>
                        <Td>
                          <Badge
                            tone={
                              document.status === "delivered"
                                ? "good"
                                : "neutral"
                            }
                          >
                            {document.status}
                          </Badge>
                        </Td>
                        <Td>{document.healthie_document_id || "Pending"}</Td>
                        <Td>{fmtDateTime(document.created_at)}</Td>
                        <Td>
                          {url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-accent hover:underline"
                            >
                              View PDF
                            </a>
                          ) : (
                            "Preparing document"
                          )}
                        </Td>
                      </tr>
                    );
                  }),
                )}
              </tbody>
            </table>
          </div>
          {orders.every((order) => !order.chart_documents.length) && (
            <p className="px-15 py-12 type-label-sm text-ink-muted">
              Lab intake documents are being prepared.
            </p>
          )}
          {orders.map((order) => (
            <div
              key={order.id}
              className="border-t border-line-subtle px-15 py-12"
            >
              <button
                type="button"
                className="type-label-sm text-accent hover:underline"
                aria-expanded={expanded === order.id}
                onClick={() => toggle(order.id)}
              >
                {order.order_number} ·{" "}
                {order.results.length
                  ? `View ${order.results.length} laboratory report records`
                  : "Laboratory report pending"}
              </button>
              {expanded === order.id && (
                <LabOrderContent
                  order={order}
                  clinician
                  adminPatientId={patientId}
                />
              )}
            </div>
          ))}
        </Card>
      </div>
    );

  return (
    <div className="ph-no-capture">
      <Card title={`Blood test orders (${orders.length})`} action={action}>
        <div className="overflow-x-auto">
          <table className="w-full type-label-sm">
            <thead className="bg-sunken">
              <tr>
                <Th>Order</Th>
                <Th>Status</Th>
                <Th>Payment</Th>
                <Th>Tracking</Th>
                <Th>Created</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <Fragment key={order.id}>
                  <tr className="border-t border-line-subtle">
                    <Td>
                      {order.order_number}
                      <div className="text-ink-muted">{order.product_name}</div>
                    </Td>
                    <Td>
                      <Badge tone={order.issue ? "warn" : "neutral"}>
                        {labOrderLabel(order)}
                      </Badge>
                      {order.provider_rejection?.address &&
                        !order.address_override?.approved && (
                          <div className="mt-4">
                            <span className="inline-flex items-center type-10 uppercase tracking-wide px-6 py-2 bg-warning-wash text-ink border-l-2 border-warning">
                              Address rejected by Tasso
                            </span>
                          </div>
                        )}
                    </Td>
                    <Td>
                      {fmtMoney(
                        order.receipt.amount_cents,
                        order.receipt.currency,
                      )}
                      <div className="text-ink-muted">
                        {order.payment_status}
                      </div>
                    </Td>
                    <Td>
                      <LabTrackingLinks order={order} />
                    </Td>
                    <Td>{fmtDateTime(order.created_at)}</Td>
                    <Td>
                      <button
                        type="button"
                        className="text-accent hover:underline"
                        aria-expanded={expanded === order.id}
                        onClick={() => toggle(order.id)}
                      >
                        {expanded === order.id ? "Hide" : "View"}
                      </button>
                    </Td>
                  </tr>
                  {expanded === order.id && (
                    <tr>
                      <td
                        colSpan={6}
                        className="border-t border-line-subtle px-15 py-12"
                      >
                        <TassoRejection order={order} patientId={patientId} />
                        <p className="type-label-sm mb-12">
                          Tasso order:{" "}
                          <span className="font-mono">
                            {order.provider_order_id || "Not submitted yet"}
                          </span>
                          {order.last_error ? ` · ${order.last_error}` : ""}
                        </p>
                        <LabOrderContent
                          order={order}
                          clinician
                          adminPatientId={patientId}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function LabTrackingLinks({ order }: { order: AdminLabOrder }) {
  const tracking = order.bundles
    .flatMap((bundle, index) => [
      { label: `Kit ${index + 1} outbound`, value: bundle.outbound_tracking },
      { label: `Kit ${index + 1} return`, value: bundle.return_tracking },
    ])
    .filter((item) => item.value);
  if (!tracking.length)
    return <span className="text-ink-muted">Tracking not yet available</span>;
  return (
    <div className="flex flex-col gap-4">
      {tracking.map((item) => {
        const url = labTrackingUrl(item.value!);
        return (
          <span key={item.label}>
            {item.label}:{" "}
            {url ? (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="text-accent hover:underline"
              >
                {item.value!.tracking_number || "Track"}
              </a>
            ) : (
              item.value!.tracking_number || "Pending"
            )}
          </span>
        );
      })}
    </div>
  );
}

function LabIntakeAnswers({ order }: { order: AdminLabOrder }) {
  const raw = order.intake?.patient_details;
  const patient =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const address = order.shipping_address;
  const rows = [
    ["Name", [patient.first_name, patient.last_name].filter(Boolean).join(" ")],
    ["Email", patient.email],
    ["Date of birth", patient.dob],
    ["Sex assigned at birth", patient.assigned_sex],
    ["Phone", patient.phone],
    [
      "Shipping address",
      address
        ? [
            address.line1,
            address.line2,
            address.city,
            address.state,
            address.postal_code,
          ]
            .filter(Boolean)
            .join(", ")
        : null,
    ],
    [
      "Lab consent",
      order.intake?.consent === true ? "Confirmed" : "Not recorded",
    ],
    ["Intake version", order.intake?.version],
  ];
  return (
    <table className="w-full type-label-sm">
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={String(label)} className="border-t border-line-subtle">
            <Th>{String(label)}</Th>
            <Td>{value == null || value === "" ? "—" : String(value)}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const NOTE_MIN = 5;
const NOTE_MAX = 500;

/** Why Tasso refused the kit, and the address override when the refusal was
 *  Tasso's USPS address check (they ship FedEx, so USPS misses some rural and
 *  new addresses that are deliverable). Renders no hooks itself, so kits
 *  without a rejection never touch the override mutation. */
function TassoRejection({
  order,
  patientId,
}: {
  order: AdminLabOrder;
  patientId: string;
}) {
  const rejection = order.provider_rejection;
  const override = order.address_override;
  if (!rejection) return null;

  if (!rejection.address)
    return (
      <div className="mb-12 border border-line-subtle border-l-4 border-l-warning bg-warning-wash px-12 py-10">
        <p className="type-label-sm text-ink font-medium">
          Tasso rejected this kit
        </p>
        <p className="type-body-sm">
          {rejection.detail || rejection.code} · {fmtDateTime(rejection.at)}
        </p>
        <p className="type-body-xs text-ink-muted">
          The kit is not at Tasso and will not ship. Contact engineering with
          this reason.
        </p>
      </div>
    );

  if (override?.approved)
    return (
      <div className="mb-12 rounded border border-line bg-sunken px-12 py-10">
        <p className="type-label-sm">Address override approved</p>
        <p className="type-body-sm">
          {override.approval
            ? `By ${override.approval.by ?? "an admin"} on ${fmtDateTime(override.approval.at)}. `
            : ""}
          {order.provider_order_id
            ? "Tasso accepted the kit."
            : "The kit goes to Tasso on the next dispatch, usually within a few minutes. Refresh to check."}
        </p>
      </div>
    );

  const delivered = override?.delivered_rx_orders_to_address ?? 0;
  return (
    <div className="mb-12 border border-line-subtle border-l-4 border-l-warning bg-warning-wash px-12 py-10 flex flex-col gap-8">
      <p className="type-label-sm text-ink font-medium">
        Shipping address rejected by Tasso — kit not sent
      </p>
      <p className="type-body-sm">
        Tasso checks addresses against USPS but ships with FedEx, so some rural
        and new addresses come back “Address Not Found” even though they are
        deliverable. Nothing has been created at Tasso yet, which is why the kit
        does not show in Ramorra.
      </p>
      <p className="type-body-sm font-medium">
        {delivered > 0
          ? `Medication has been delivered to this same street address ${delivered} time${delivered === 1 ? "" : "s"}, so it is deliverable.`
          : "No medication has been delivered to this address yet. Confirm it with the patient before overriding."}
      </p>
      <p className="type-body-xs text-ink-muted">
        Tasso’s reason: {rejection.detail || rejection.code} ·{" "}
        {fmtDateTime(rejection.at)}. If the address itself is wrong, do not
        override: ask engineering to correct the kit’s address.
      </p>
      {override?.eligible ? (
        <AddressOverrideForm
          orderId={order.id}
          patientId={patientId}
          needsPatientConfirmation={delivered === 0}
        />
      ) : (
        <p className="type-body-xs text-ink-muted">
          An override is not available for this kit (it is already at Tasso,
          refunded or unpaid).
        </p>
      )}
    </div>
  );
}

function AddressOverrideForm({
  orderId,
  patientId,
  needsPatientConfirmation,
}: {
  orderId: string;
  patientId: string;
  needsPatientConfirmation: boolean;
}) {
  const approve = useApproveLabAddressOverride(patientId);
  const [note, setNote] = useState("");
  const [patientConfirmed, setPatientConfirmed] = useState(false);
  const trimmed = note.trim();
  const ready =
    trimmed.length >= NOTE_MIN &&
    (!needsPatientConfirmation || patientConfirmed) &&
    !approve.isPending;
  return (
    <>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={approve.isPending}
        rows={2}
        maxLength={NOTE_MAX}
        placeholder="How you confirmed the address (e.g. 2 UPS deliveries there, or the patient confirmed it)"
        className="px-10 py-8 bg-white border border-line type-body-sm rounded focus:outline-none focus:border-ink resize-y"
      />
      {needsPatientConfirmation && (
        <label className="flex items-center gap-8 type-body-sm">
          <input
            type="checkbox"
            checked={patientConfirmed}
            onChange={(e) => setPatientConfirmed(e.target.checked)}
            disabled={approve.isPending}
          />
          I confirmed this exact address with the patient
        </label>
      )}
      <div className="flex items-center gap-12">
        <button
          type="button"
          className="h-36 px-15 bg-ink text-page type-label-sm rounded hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          disabled={!ready}
          onClick={() =>
            approve.mutate({ orderId, note: trimmed, patientConfirmed })
          }
        >
          {approve.isPending
            ? "Approving…"
            : "Address is deliverable, resend to Tasso"}
        </button>
        <span className="type-body-xs text-ink-muted">
          Sends the kit with Tasso’s address check skipped. Recorded with your
          name and note.
        </span>
      </div>
      {approve.isError && (
        <p className="type-body-sm text-danger">
          {approve.error instanceof Error && approve.error.message
            ? approve.error.message.slice(0, 300)
            : "Could not approve the override. Refresh and try again."}
        </p>
      )}
    </>
  );
}
